import FJUtil from './FJUtil';
import { AI_GENERATOR_VIDEO_TAB_TYPE } from '../config/dataConfig/FJAIVideoGeneratorData.js';
import { FJDecoderManager } from '@fj/codec';
import FJAudioUtil from './FJAudioUtil.js';

const FJVideoUtil = {
    /**
     * @description 返回解码视频帧类型
     * @returns
     */
    getDecodeVideoFrameType: function () {
        if (FJUtil.getBrowser() === 'FF') {
            return 'IMAGEBITMAP';
        }

        if (FJUtil.couldUseWebCodecs('decode') && typeof VideoFrame !== 'undefined') {
            return 'VIDEOFRAME';
        }
        if (
            typeof ImageBitmap !== 'undefined' &&
            typeof createImageBitmap !== 'undefined' &&
            typeof OffscreenCanvas !== 'undefined'
        ) {
            return 'IMAGEBITMAP';
        }
        return 'TYPEDARRAY';
    },

    getVideoDurationWithUrl: function (url) {
        return new Promise((resolve, reject) => {
            let video = document.createElement('video');

            function addEvent() {
                FJUtil.addEvent(video, 'loadeddata', onLoaded);
                FJUtil.addEvent(video, 'error', onError);
            }

            function removeEvent() {
                FJUtil.removeEvent(video, 'loadeddata', onLoaded);
                FJUtil.removeEvent(video, 'error', onError);
                video.src = '';
                video = null;
            }

            function onLoaded() {
                let duration = video.duration;
                removeEvent();
                resolve(duration);
            }

            function onError(e) {
                removeEvent();
                console.log(e);
                reject(new Error('getAudioDurationWithUrl error'));
            }

            addEvent();
            video.src = url;
        });
    },

    getAudioFromVideo: async function (file) {
        try {
            const blobUrl = URL.createObjectURL(file);
            const videoDuration = await this.getVideoDurationWithUrl(blobUrl);
            const arrayBuffer = await FJDecoderManager.decodeAudioOverall(file, {
                start: 0,
                end: videoDuration,
                samplerate: FJUtil.getAudioSampleRate(),
                mode: 'FFMPEG',
                speed: 1,
                fileType: 'video',
                ignoreNoValidDuration: true,
            });
            const wavBuffer = FJAudioUtil.encodeWAV(new Float32Array(arrayBuffer));
            const mimeType = 'audio/wav';
            const blob = new Blob([new DataView(wavBuffer)], {
                type: mimeType,
            });
            const audioFile = new File([blob], FJUtil.getFileName(file.name, true) + '.wav', { type: mimeType });
            return audioFile;
        } catch (e) {
            throw new Error(e);
        }
    },

    /**
     * @description 加载视频
     * @param {File} file
     * @returns
     */
    loadVideo: function (file) {
        return new Promise((resolve, reject) => {
            const video = document.createElement('video');

            video.onloadedmetadata = () => {
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                    duration: video.duration,
                    src: video.src,
                    name: file.name,
                });
            };
            video.src = URL.createObjectURL(file);
            video.onerror = () => {
                reject(new Error('Failed to load video'));
            };
        });
    },

    getAiGeneratorVideoTab: function () {
        let defaultTabIndex = AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO;
        if (location.pathname.includes('image-to-video')) {
            defaultTabIndex = AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO;
        } else if (location.pathname.includes('text-to-video')) {
            defaultTabIndex = AI_GENERATOR_VIDEO_TAB_TYPE.AI_TEXT_TO_VIDEO;
        } else if (location.pathname.includes('video-effect')) {
            defaultTabIndex = AI_GENERATOR_VIDEO_TAB_TYPE.AI_EFFECTS_VIDEO;
        } else if (location.pathname.includes('video-generator')) {
            defaultTabIndex = AI_GENERATOR_VIDEO_TAB_TYPE.AI_IMAGE_TO_VIDEO;
        }
        return defaultTabIndex;
    },

    /**
     * @description 获取视频元数据信息
     * @param {File} file
     * @returns
     */
    getMetadataInfo: async function (file) {
        try {
            const info = await FJDecoderManager.getMetadataInfo(file);
            if (info.error) {
                throw new Error(info.error);
            }
            return info;
        } catch (e) {
            return null;
        }
    },

    /**
     * @description 判断视频文件是否是特殊格式 || 是否需要转码，目前只判断是否是HLG视频 用于视频去背景拦截不支持视频格式
     * @param {*} file
     * @returns
     */
    checkVideoFileIsSpecialFormat: function (file) {
        return new Promise(async resolve => {
            try {
                const mediaInfo = await this.getMetadataInfo(file);
                if (!mediaInfo) {
                    resolve(true);
                    return;
                }
                const { streams } = mediaInfo;

                let isHDR = false;
                for (let i = 0; i < streams.length; i++) {
                    const { color_transfer } = streams[i];
                    if (color_transfer) {
                        // 1. 检查色彩转换特性
                        isHDR = color_transfer === 'arib-std-b67'; // HLG
                        // color_transfer === 'smpte2084' || // HDR10/HDR10+
                        // color_transfer === 'smpte428'; // Dolby Vision
                    }
                    if (isHDR) {
                        resolve(true);
                        return;
                    }
                    // if (color_space) {
                    //     // 2. 检查色彩空间
                    //     isHDR = color_space === 'bt2020nc' || color_space === 'bt2020c';
                    // }
                    // if (isHDR) {
                    //     resolve(true);
                    //     return;
                    // }
                    // if (color_primaries) {
                    //     // 3. 检查色彩原色
                    //     isHDR = color_primaries === 'bt2020';
                    // }
                    // if (isHDR) {
                    //     resolve(true);
                    //     return;
                    // }
                }

                resolve(false);

                // // 判断是不是带有透明通道的mov视频
                // let isAlphaOfMov = false;

                // // 检查文件扩展名是否为mov
                // const fileName = file.name.toLowerCase();
                // const isMovFile = fileName.endsWith('.mov');

                // if (isMovFile) {
                //     // 检查视频流中是否包含alpha通道
                //     for (let i = 0; i < streams.length; i++) {
                //         const stream = streams[i];
                //         if (
                //             stream.pix_fmt &&
                //             (stream.pix_fmt.includes('rgba') ||
                //                 stream.pix_fmt.includes('yuva') ||
                //                 stream.pix_fmt.includes('_alpha'))
                //         ) {
                //             isAlphaOfMov = true;
                //             break;
                //         }
                //     }
                // }

                // resolve(isAlphaOfMov);
            } catch (e) {
                resolve(false);
            }
        });
    },
    releaseVideo: function (mediaEl) {
        if (!mediaEl) return;
        mediaEl.pause();
        if (mediaEl.src.startsWith('blob:')) {
            URL.revokeObjectURL(mediaEl.src); // 释放 Blob 资源（关键！避免永久占用内存）
        }
        mediaEl.src = '';
        mediaEl.load(); // 强制释放解码器和 GPU 纹理内存
    },
};

export default FJVideoUtil;
